import { useState } from "react";
import { message } from "antd";
import {
  MailOutlined, SafetyCertificateOutlined, ArrowRightOutlined, ArrowLeftOutlined, LoadingOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "../assets/img/om-full-primary-logo.svg";
import OtpInput from "../components/OtpInput";
import { AuthService } from "../services/corpClaimPortalService";
import { useAuth } from "../context/AuthContext";
import { maskEmail, useResendCooldown } from "./Authentication/otpHelpers";
import "./Authentication/auth.css";

/**
 * OTP login for HR/Broker/CRM. Same shell, copy rhythm and OTP-box component
 * as EasyLife's own closed-portal logins (Vendor/ForeignAgent) — the intent
 * was for this to read as the same product family, not a bespoke screen.
 */
const Login = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("login");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { cooldown, start: startCooldown } = useResendCooldown();

  const send = async (fn, successMsg) => {
    if (!email.trim()) {
      message.error("Enter your work email address.");
      return;
    }
    setLoading(true);
    try {
      await fn(email.trim());
      setStep("otp");
      startCooldown();
      message.success(successMsg);
    } catch (e) {
      message.error(e?.response?.data?.message || "Could not send the code.");
    } finally {
      setLoading(false);
    }
  };

  const requestOtp = () => send(AuthService.requestOtp, "A one-time code has been sent to you.");
  const resendOtp = () => send(AuthService.resendOtp, "A new code has been sent.");

  const verifyOtp = async () => {
    setLoading(true);
    try {
      const res = await AuthService.verifyOtp(email.trim(), otp);
      login(res.token, res.user);
      const redirectTo = location.state?.from || "/dashboard";
      navigate(redirectTo, { replace: true });
    } catch (e) {
      message.error(e?.response?.data?.message || "That code is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-container">
      <div className="auth-card-wrapper">
        <div className="auth-sidebar">
          <div className="sidebar-header">
            <img src={Logo} alt="Old Mutual" className="sidebar-logo" />
          </div>
          <div className="sidebar-visual">
            <div className="floating-card-visual">
              <div className="card-content">
                <div className="card-shine"></div>
                <p className="card-text">Every claim you submit, tracked in one place.</p>
              </div>
            </div>
          </div>
          <div className="sidebar-footer">
            <div className="quote-icon">&#10077;</div>
            <p className="quote-text">
              Submit a claim, upload the supporting documents and follow it
              through to payment — all from a single portal.
            </p>
            <p className="quote-author">Old Mutual &middot; Corporate Claims</p>
          </div>
        </div>

        <div className="auth-main-content">
          <div className="content-inner">
            <div className="auth-form">
              <div className="auth-form__badge">
                {step === "login" ? <MailOutlined /> : <SafetyCertificateOutlined />}
              </div>

              <h1 className="auth-form__title">
                {step === "login" ? "Corporate Claims" : "Verify it's you"}
              </h1>
              <p className="auth-form__subtitle">
                {step === "login" ? (
                  "Sign in with the email your account was registered with to receive a one-time code."
                ) : (
                  <>We sent a 4-digit code to <strong>{maskEmail(email)}</strong>.</>
                )}
              </p>

              <div className="auth-steps">
                <span className={`auth-steps__seg ${step === "login" ? "is-active" : "is-done"}`} />
                <span className={`auth-steps__seg ${step === "otp" ? "is-active" : ""}`} />
              </div>

              {step === "login" ? (
                <div>
                  <label className="auth-label">Work email</label>
                  <div className="auth-field">
                    <span className="auth-field__icon"><MailOutlined /></span>
                    <input
                      className="auth-field__input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      onKeyDown={(e) => e.key === "Enter" && requestOtp()}
                      autoFocus
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="auth-label">One-time code</label>
                  <OtpInput value={otp} onChange={setOtp} length={4} disabled={loading} />
                  <div className="auth-resend">
                    Didn&apos;t receive it?{" "}
                    <button onClick={resendOtp} disabled={loading || cooldown > 0}>
                      {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                    </button>
                  </div>
                  <button
                    className="auth-back"
                    onClick={() => { setStep("login"); setOtp(""); }}
                    disabled={loading}
                  >
                    <ArrowLeftOutlined /> Use a different email
                  </button>
                </div>
              )}

              <button
                className="auth-btn"
                onClick={() => (step === "login" ? requestOtp() : verifyOtp())}
                disabled={loading || (step === "otp" && otp.length < 4)}
              >
                {loading ? (
                  <><LoadingOutlined /> Please wait&hellip;</>
                ) : step === "login" ? (
                  <>Request Code <ArrowRightOutlined /></>
                ) : (
                  <>Verify &amp; Continue <ArrowRightOutlined /></>
                )}
              </button>

              <p className="auth-form__foot">Old Mutual Ghana &middot; Corporate Claims Portal</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
